// Member-initiated invite requests (CYBORG-1087): a member asks the owners/admins
// to invite an email address; an admin approves (the normal email invite goes out)
// or declines. RPC facades plus a workspace-scoped live store, the same shape as
// join-requests.svelte.ts, so the Members "Pending requests" list and the Settings
// badge stay live through the invite_request_changed push (app.svelte.ts).
import { client } from "./client.js";
import { workspaceState } from "../core/state.svelte.js";
import { inviteMember } from "./invitations.js";
import type { WorkspaceInviteRequestView } from "../core/client.js";

export type { WorkspaceInviteRequestView };

export class InviteRequestsState {
  requests: WorkspaceInviteRequestView[] = $state([]);
  workspaceId: string | null = $state(null);

  load(workspaceId: string, requests: WorkspaceInviteRequestView[]): void {
    this.workspaceId = workspaceId;
    this.requests = requests;
  }

  // A push for a workspace this store is not tracking is dropped (no badge bleed).
  upsert(request: WorkspaceInviteRequestView): void {
    if (this.workspaceId !== null && request.workspaceId !== this.workspaceId) return;
    const idx = this.requests.findIndex((r) => r.id === request.id);
    if (idx === -1) {
      this.requests = [request, ...this.requests];
    } else {
      const next = this.requests.slice();
      next[idx] = request;
      this.requests = next;
    }
  }

  pendingFor(workspaceId: string | undefined): WorkspaceInviteRequestView[] {
    if (!workspaceId || this.workspaceId !== workspaceId) return [];
    return this.requests.filter((r) => r.status === "pending");
  }

  pendingCount(workspaceId: string | undefined): number {
    return this.pendingFor(workspaceId).length;
  }

  clear(): void {
    this.requests = [];
    this.workspaceId = null;
  }
}

export const inviteRequestsState = new InviteRequestsState();

function targetWorkspace(workspaceId?: string): string {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  return wsId;
}

export async function requestInvite(
  email: string,
  note?: string,
  workspaceId?: string,
): Promise<WorkspaceInviteRequestView> {
  const { request } = await client.requestInvite(targetWorkspace(workspaceId), email, note);
  return request;
}

export async function listInviteRequests(
  status?: "pending" | "approved" | "declined",
  workspaceId?: string,
): Promise<WorkspaceInviteRequestView[]> {
  const { requests } = await client.listInviteRequests(targetWorkspace(workspaceId), status);
  return requests;
}

export async function declineInviteRequest(
  request: WorkspaceInviteRequestView,
): Promise<WorkspaceInviteRequestView> {
  const { request: decided } = await client.decideInviteRequest(
    request.workspaceId,
    request.id,
    "declined",
  );
  return decided;
}

// Approve = the existing email-invite path (as Member), then mark the request
// approved. The relay refuses the approval unless that invite really exists.
export async function approveInviteRequest(
  request: WorkspaceInviteRequestView,
): Promise<WorkspaceInviteRequestView> {
  // inviteMember targets the SELECTED workspace, so never approve across workspaces.
  if (workspaceState.current?.id !== request.workspaceId) {
    throw new Error("Open this workspace to approve its requests.");
  }
  try {
    await inviteMember(request.email, "member");
  } catch (err) {
    // Someone who joined in the meantime needs no invite; the request is still handled.
    if (!(err instanceof Error && err.message === "already a member")) throw err;
  }
  const { request: decided } = await client.decideInviteRequest(
    request.workspaceId,
    request.id,
    "approved",
  );
  return decided;
}

// The relay answers with short machine strings; turn them into sentences.
export function inviteRequestErrorMessage(err: unknown, email?: string): string {
  const raw = err instanceof Error ? err.message : "";
  const who = email?.trim() || "That address";
  switch (raw) {
    case "forbidden":
      return "Your role can't request invites in this workspace.";
    case "already a member":
      return `${who} is already in this workspace.`;
    case "already invited":
      return `${who} already has a pending invite.`;
    case "invalid email address":
    case "email required":
      return "Enter a valid email address.";
    case "note too long":
      return "Keep the note under 500 characters.";
    case "invite not sent":
      return "The invite didn't go out. Try again.";
    case "request already decided":
      return "Someone already handled this request.";
    default:
      return raw || "Something went wrong. Try again.";
  }
}
